import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { EMIR_CLASSIFICATIONS, EMIR_THRESHOLDS, EmirClassification, ESMA_CLASSIFICATIONS, NACE_CLASSIFICATIONS } from '@/constants/institution';
import type { InstitutionFuturesReporting, InstitutionFuturesReportingOrDefaults } from '@/features/institution/futures/reporting/schema';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS } from '@/services/api/profile';

const InstitutionFuturesReportingFieldsetGeneralLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.reporting.title')}</PageHeadingTitle>
      </legend>
    </PageHeading>
  );
};
InstitutionFuturesReportingFieldsetGeneralLegend.displayName = 'InstitutionFuturesReportingFieldsetGeneralLegend';

const InstitutionFuturesReportingFieldsetGeneral = () => {
  const form = useFormContext<InstitutionFuturesReportingOrDefaults>();
  const { setValue } = form;
  const { t } = useTranslation();
  const emir = form.watch('emir');
  const emirClassification = form.watch('emirClassification');

  const resetEmirFieldset = useCallback(() => {
    setValue('emirUtiAcknowledgment', undefined);
    setValue('emirPostTradeRiskConfirmation', undefined);
  }, [setValue]);

  return (
    <div className="space-y-6">
      <FormField
        name="mifid"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.reporting.mifid')}*</FormLabel>
            <FormControl>
              <RadioGroup defaultValue={field.value} variant="cards" aria-label={t('institutionDetails.reporting.mifid')} onValueChange={field.onChange}>
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="emir"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.reporting.emir')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.reporting.emir')}
                onValueChange={(value) => {
                  field.onChange(value);
                  if (value === 'NO') resetEmirFieldset();
                }}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="emirClassification"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.reporting.emirDetails.emirClassification.title')}*</FormLabel>
            <FormDescription>{t('institutionDetails.reporting.emirDetails.emirClassification.description')}</FormDescription>
            <Select
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);

                if (value !== EmirClassification.NON_FINANCIAL_COUNTERPARTY) {
                  setValue('naceClassification', undefined);
                  setValue('emirRiskReductionConfirmation', undefined);
                }
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('institutionDetails.reporting.emirDetails.placeholders.emirClassification')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {EMIR_CLASSIFICATIONS.map((classification) => (
                  <SelectItem key={classification} value={classification}>
                    {t(`institutionDetails.reporting.emirDetails.emirClassification.options.${classification}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {emirClassification === EmirClassification.FINANCIAL_COUNTERPARTY ? (
        <FormField
          name="esmaClassification"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.reporting.emirDetails.esmaClassification.title')}*</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('institutionDetails.reporting.emirDetails.placeholders.esmaClassification')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ESMA_CLASSIFICATIONS.map((classification) => (
                    <SelectItem key={classification} value={classification}>
                      {t(`institutionDetails.reporting.emirDetails.esmaClassification.options.${classification}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : emirClassification === EmirClassification.NON_FINANCIAL_COUNTERPARTY ? (
        <>
          <FormField
            name="naceClassification"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.reporting.emirDetails.naceClassification.title')}*</FormLabel>
                <Select defaultValue={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('institutionDetails.reporting.emirDetails.placeholders.naceClassification')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {NACE_CLASSIFICATIONS.map((classification) => (
                      <SelectItem key={classification} value={classification}>
                        {t(`institutionDetails.reporting.emirDetails.naceClassification.options.${classification}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="emirRiskReductionConfirmation"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.reporting.emirDetails.emirRiskReductionConfirmation')}*</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.reporting.emirDetails.emirRiskReductionConfirmation')}
                    onValueChange={field.onChange}
                  >
                    {EMIR_RISK_REDUCTION_CONFIRMATION_OPTIONS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}
      <FormField
        name="emirThreshold"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.reporting.emirDetails.emirThreshold.title')}*</FormLabel>
            <FormControl>
              <RadioGroup defaultValue={field.value} variant="cards" aria-label={t('institutionDetails.reporting.emirDetails.emirThreshold.title')} onValueChange={field.onChange}>
                {EMIR_THRESHOLDS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`institutionDetails.reporting.emirDetails.emirThreshold.options.${answer}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {emir === 'YES' ? (
        <FormField
          name="emirReportingResponsibility"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.reporting.emirDetails.emirReportingResponsibility')}*</FormLabel>
              <FormControl>
                <RadioGroup
                  defaultValue={field.value}
                  variant="cards"
                  aria-label={t('institutionDetails.reporting.emirDetails.emirReportingResponsibility')}
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (value === 'NO') resetEmirFieldset();
                  }}
                >
                  {BINARY_ANSWERS.map((answer) => (
                    <FormItem key={answer}>
                      <RadioGroupItemCard variant="outline">
                        <FormControl>
                          <RadioGroupItem value={answer} />
                        </FormControl>
                        <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                      </RadioGroupItemCard>
                    </FormItem>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionFuturesReportingFieldsetGeneral.displayName = 'InstitutionFuturesReportingFieldsetGeneral';

const beforeStepLeave: FormStepGuard<InstitutionFuturesReporting> = async ({ direction, form, onSubmit }, next) => {
  if (!form || (direction === 'next' && !(await form.trigger()))) return next(false);

  if (form.getValues('emir') === 'NO' || form.getValues('emirReportingResponsibility') === 'YES') {
    await onSubmit(form.getValues());
    return next(false);
  }

  return next();
};

InstitutionFuturesReportingFieldsetGeneral.beforeStepLeave = beforeStepLeave;

export { InstitutionFuturesReportingFieldsetGeneral, InstitutionFuturesReportingFieldsetGeneralLegend };
